import type {
    ColumnListPlugin,
    ColumnListPluginContext,
    ColumnPlugin,
    ColumnPluginContext,
    HeaderPlugin,
} from "app/pages/sales-intelligence/common-components/column-picker/plugins/types";
import {
    calculateTotalDataPointsCost,
    getDataPointCost,
} from "app/pages/sales-intelligence/modules/quota/utils/dataCredits";
import { formatCompactNumber } from "app/pages/sales-intelligence/utils/numbers";

import ExportCostToggle from "../../components/QuotaCostToggle/ExportCostToggle";
import QuotaTag from "../../components/QuotaTag/QuotaTag";
import { quotaKeys } from "../../utils/i18n";

import type {
    DataCreditsColumnPickerPlugins,
    DataCreditsPluginConfig,
    ToggleHeaderConfig,
    TotalCostBadgeConfig,
} from "./types";

export const dataCreditsColumnPickerPlugins: DataCreditsColumnPickerPlugins = {
    createCostBadge: <T extends string>(config: DataCreditsPluginConfig<T>): ColumnPlugin => ({
        id: "data-credits-badge",
        slot: "column-badge",
        behavior: "extend",
        shouldRender: () => config.isCostVisible,
        render: (context: ColumnPluginContext) => {
            const cost = getDataPointCost(config.dataPoints, context.column.type, config.costTypeMapper);

            return {
                component:
                    cost == null ? null : (
                        <QuotaTag
                            size="sm"
                            type="light"
                            title={formatCompactNumber(cost)}
                        />
                    ),
                priority: 5,
            };
        },
    }),

    createCostToggle: (config: ToggleHeaderConfig): HeaderPlugin => ({
        id: "data-credits-toggle",
        slot: "header-action",
        render: () => ({
            component: (
                <ExportCostToggle
                    isSelected={config.isChecked}
                    onClick={config.onToggle}
                />
            ),
            priority: 1,
        }),
    }),

    createTotalCostBadge: <T extends string>(config: TotalCostBadgeConfig<T>): ColumnListPlugin => ({
        id: "total-cost-badge",
        slot: "column-list-badge",
        shouldRender: () => config.isCostVisible,
        render: (context: ColumnListPluginContext) => {
            const totalCost =
                calculateTotalDataPointsCost(
                    config.dataPoints,
                    context.columns.map(({ type }) => type),
                    config.costTypeMapper,
                ) ?? 0;

            const totalRowCostKey = quotaKeys.totalRowCost({ cost: formatCompactNumber(totalCost) });

            return {
                component:
                    config.isCostVisible && config.dataPoints ? (
                        <QuotaTag
                            size="sm"
                            type="light"
                            title={config.translate(totalRowCostKey.key, totalRowCostKey.replacements)}
                        />
                    ) : null,
                priority: 10,
            };
        },
    }),
};
